import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowLeftRight, Landmark, SearchX } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { accountIdentifier, readShowBalances } from "@/components/BankConnectionsPage";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import {
  DateRangeFilter,
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  dateRangeQuery,
  type DateRange,
} from "@/components/PageUi";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { usePermissions } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type BankAccount = components["schemas"]["BankAccountResponse"];
type BankTransaction = components["schemas"]["BankTransactionResponse"];

const PAGE_SIZE = 50;

// Accounts on a disconnected or revoked connection are not readable, matching the bank accounts list.
const UNREADABLE = ["Disconnected", "Revoked"];

export function BankAccountPage({ accountId, onBack }: { accountId: string; onBack: () => void }) {
  const api = useDolfinClient();
  const { roleAllows } = usePermissions();
  // Seeing balances is a read: it follows the role, not the demo write gate.
  const balancesVisible = roleAllows("connectBank") && readShowBalances();
  const [account, setAccount] = useState<BankAccount | null>(null);
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  const [accountState, setAccountState] = useState<"loading" | "missing" | "ready">("loading");
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dates, setDates] = useState<DateRange>({});
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // The connections list carries each connection's accounts, so one call gives the account and its bank's name.
  useEffect(() => {
    let cancelled = false;
    api.GET("/v1/bank-connections").then(({ data }) => {
      if (cancelled) return;
      const connection = (data?.data ?? []).find(
        (c) => !UNREADABLE.includes(c.state) && c.accounts.some((a) => a.id === accountId),
      );
      const found = connection?.accounts.find((a) => a.id === accountId) ?? null;
      setAccount(found);
      setInstitutionName(connection?.institutionName ?? null);
      setAccountState(found ? "ready" : "missing");
    });
    return () => {
      cancelled = true;
    };
  }, [api, accountId]);

  const fetchTransactions = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE, ...dateRangeQuery(dates) };
      if (cursor) query.Cursor = cursor;
      try {
        const { data, error: apiError } = await api.GET("/v1/bank-accounts/{id}/transactions", {
          params: { path: { id: accountId }, query },
        });
        if (apiError || !data) {
          setError(apiErrorMessage(apiError, "Failed to load transactions"));
          return;
        }
        setTransactions(data.data ?? []);
        setNextCursor(data.meta?.cursors?.next ?? null);
      } catch {
        setError("Failed to load transactions");
      } finally {
        setLoading(false);
      }
    },
    [api, accountId, dates],
  );

  useEffect(() => {
    if (accountState !== "ready") return;
    setCursorStack([]);
    setNextCursor(null);
    fetchTransactions();
  }, [fetchTransactions, accountState]);

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      Back to bank connections
    </button>
  );

  if (accountState === "loading") {
    return (
      <PageContainer>
        {back}
        <LoadingState label="Loading account..." />
      </PageContainer>
    );
  }

  if (accountState === "missing" || !account) {
    return (
      <PageContainer>
        {back}
        <EmptyState icon={Landmark} title="Account not available">
          This account is not connected any more. Connect its bank again to see its transactions.
        </EmptyState>
      </PageContainer>
    );
  }

  const filtered = !!(dates.from || dates.to);

  return (
    <PageContainer>
      {back}
      <PageHeader
        eyebrow={institutionName ?? "Bank account"}
        title={account.name}
        subtitle={
          <>
            <span className="font-mono text-xs whitespace-pre">{accountIdentifier(account)}</span>
            <span className="block text-xs">
              {account.transactionsSyncedAt
                ? `Transactions synced ${formatDateTime(account.transactionsSyncedAt)}`
                : "Transactions sync right after connecting, then every day."}
            </span>
          </>
        }
        actions={
          balancesVisible && account.balance != null ? (
            <div className="text-right">
              <div className="text-lg font-semibold tabular-nums">{formatCurrency(account.balance, account.currency)}</div>
              {account.balanceAsOf ? (
                <div className="text-xs text-muted-foreground">As of {formatDateTime(account.balanceAsOf)}</div>
              ) : null}
            </div>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <DateRangeFilter value={dates} onChange={setDates} />
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchTransactions(cursorStack[cursorStack.length - 1])} />
      ) : loading ? (
        <LoadingState label="Loading transactions..." />
      ) : transactions.length === 0 ? (
        filtered ? (
          <EmptyState icon={SearchX} title="No transactions in those dates">
            Nothing was booked on this account between those dates.
          </EmptyState>
        ) : (
          <EmptyState icon={ArrowLeftRight} title="No transactions yet">
            Transactions from the last 90 days sync right after connecting, then every day.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Date</TableHead>
                  <TableHead className={TH}>Description</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                  {balancesVisible ? <TableHead className={cn(TH, "text-right")}>Balance</TableHead> : null}
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatDate(t.bookedAt)}</TableCell>
                    <TableCell className="max-w-[420px]">
                      <EntityCell
                        name={t.counterpartyName || t.description}
                        fallback="No description"
                        secondary={t.counterpartyName ? t.description : t.reference}
                        badges={
                          t.status === "Pending" ? (
                            <Badge
                              variant="outline"
                              className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                            >
                              Pending
                            </Badge>
                          ) : null
                        }
                      />
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right font-semibold whitespace-nowrap tabular-nums",
                        Number(t.amount) > 0 && "text-emerald-700 dark:text-emerald-300",
                      )}
                    >
                      {Number(t.amount) > 0 ? "+" : ""}
                      {formatCurrency(t.amount, t.currency)}
                    </TableCell>
                    {balancesVisible ? (
                      <TableCell className="text-right text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                        {t.balanceAfter != null ? formatCurrency(t.balanceAfter, t.currency) : "-"}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={cursorStack.length + 1}
            hasPrevious={cursorStack.length > 0}
            hasNext={!!nextCursor}
            disabled={loading}
            onPrevious={() => {
              const stack = [...cursorStack];
              stack.pop();
              setCursorStack(stack);
              fetchTransactions(stack[stack.length - 1]);
            }}
            onNext={() => {
              if (!nextCursor) return;
              setCursorStack((prev) => [...prev, nextCursor]);
              fetchTransactions(nextCursor);
            }}
          />
        </>
      )}
    </PageContainer>
  );
}
